import clsx from 'clsx'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '@iconify/react'

import { Tooltip } from '../../../atoms'

import type { Keyword } from '@pepper/utils/src/types/seo'

import styles from './KeywordBadge.module.css'

interface Props {
  data: Keyword
  activeCount?: number
  activeBadge?: string
  isDelete?: boolean
  handleClick?: (keyword: string) => void
  handleDelete?: () => void
  backgroundType?: 'default' | 'gray'
  isActiveHeadingKeyword?: boolean
  showCopyLink?: boolean
  showOnlyCount?: boolean
}

const CLIPBOARD_COPY_TIMEOUT = 2000

// TODO: @narharik - refactor this component
function KeywordBadge({
  data,
  activeCount,
  activeBadge,
  isDelete,
  backgroundType,
  handleClick,
  handleDelete,
  isActiveHeadingKeyword,
  showCopyLink = false,
  showOnlyCount = false
}: Props) {
  const badgeStatusRef = useRef<HTMLElement>(null)
  const [badgeStatusWidth, setBadgeStatusWidth] = useState(0)
  const [isCopied, setIsCopied] = useState(false)

  useLayoutEffect(() => {
    if (!badgeStatusRef.current) return
    setBadgeStatusWidth(badgeStatusRef.current.getBoundingClientRect().width)
  }, [])

  const percentage = useMemo(() => {
    if (!activeCount) return null
    const mean = Math.floor((data.min + data.max) / 2)
    return (activeCount / mean) * 100
  }, [activeCount, data.max, data.min])

  const handleCopyKeyword = async (keyword: string) => {
    await navigator.clipboard.writeText(keyword)
    setIsCopied(true)
    setTimeout(() => {
      setIsCopied(false)
    }, CLIPBOARD_COPY_TIMEOUT)
  }

  const getBorderColor = (
    percent: number | null,
    activeEditorCount: number,
    maxCount: number
  ) => {
    if (percent === null) return 'gray'
    if (!activeEditorCount) return 'gray'
    else if (activeEditorCount > maxCount && percent >= 500) return 'orange'
    else if (activeEditorCount >= maxCount && percent < 500) return 'green'
    else if (activeEditorCount <= maxCount && percent >= 500) return 'green'
    else if (activeEditorCount <= maxCount && percent < 500) {
      if (percent > 0 && percent < 33) return 'orange'
      else if (percent >= 33 && percent < 66) return 'yellow'
      else {
        return 'green'
      }
    } else return 'gray'
  }

  const badgeStatus =
    data.min === data.max ? data.max : `${data.min}-${data.max}`

  const badge = (
    <span
      className={clsx(
        styles.badgeContainer,
        showCopyLink && styles.badgeContainerCopyType
      )}
    >
      {isDelete && handleDelete ? (
        <button onClick={handleDelete} className={clsx(styles.deleteBtn)}>
          <Icon
            icon="ic:round-clear"
            color="var(--color-blue-500)"
            width={16}
          />
        </button>
      ) : null}

      <button
        className={clsx(
          styles.badgeText,
          activeCount === 0
            ? styles.disabledBadge
            : activeBadge === data.keyword
            ? styles.badgeActive
            : null,
          !isDelete ? `${styles.borderRadius} ${styles.borderLeft}` : null,
          backgroundType === 'gray'
            ? styles.stateDisabledGray
            : styles.stateDefaultWhite
        )}
        onClick={() => {
          if (activeCount && activeCount > 0) handleClick?.(data.keyword)
        }}
      >
        <span className={styles.keywordText}>{data.keyword}</span>
        {data.heading_presence >= 2 ? (
          <span
            className={clsx(
              styles.headerPresenceTag,
              isActiveHeadingKeyword === false
                ? styles.headerPresenceTagDisabled
                : styles.headerPresenceTagActive
            )}
          >
            H
          </span>
        ) : null}
      </button>

      {showCopyLink && (
        <button
          className={clsx(styles.copyBtn, {
            [styles.copied]: isCopied
          })}
          style={{ width: badgeStatusWidth }}
          onClick={() => handleCopyKeyword(data.keyword)}
          title="Copy Keyword"
          disabled={isCopied}
        >
          {isCopied ? (
            <Icon icon="lucide:check" width={16} height={16} />
          ) : (
            <Icon icon="lucide:copy" width={16} height={16} />
          )}
        </button>
      )}

      <span
        className={clsx(
          styles.badgeStatus,
          activeCount === 0
            ? styles.disabledBadge
            : activeBadge === data.keyword
            ? styles.badgeActive
            : null,
          activeCount !== undefined
            ? styles[getBorderColor(percentage, activeCount, data.max)]
            : styles.gray,
          backgroundType === 'gray'
            ? styles.stateDisabledGray
            : styles.stateDefaultWhite
        )}
        ref={badgeStatusRef}
      >
        {activeCount !== undefined
          ? `${activeCount}/${badgeStatus}`
          : badgeStatus}
      </span>
    </span>
  )

  if (showOnlyCount) {
    return (
      <button
        className={clsx(
          styles.keywordPillBtn,
          activeCount === 0 ? styles.cursorDisabled : null
        )}
        disabled={activeCount === 0}
        onClick={() => handleClick?.(data.keyword)}
      >
        <div
          className={clsx(
            styles.keywordPill,
            styles.keywordPillLeft,
            activeBadge === data.keyword ? styles.badgeActiveLeft : null
          )}
        >
          {data.keyword}
        </div>
        <div
          className={clsx(
            styles.keywordPill,
            styles.keywordPillRight,
            activeBadge === data.keyword ? styles.badgeActiveRight : null
          )}
        >
          {activeCount}
        </div>
      </button>
    )
  }

  if (badgeStatus === 0) return null
  if (data.search_volume) {
    return (
      <Tooltip
        className={styles.tooltip}
        title={`Search Volume: ${data.search_volume}`}
      >
        {badge}
      </Tooltip>
    )
  }

  return badge
}

export default KeywordBadge
